import {APPOINTMENTS_MAX_SIZE} from '_components/admin/components/definitions';
import {
  formatDate,
  formatDateWithoutTimezone,
} from '_components/admin/components/utils/helper';
import {combineReducers} from 'redux';
import {
  CANCEL_APPOINTMENT_SUCCESS,
  EXPORT_APPOINTMENTS_ASYNC_START,
  EXPORT_APPOINTMENTS_ASYNC_SUCCESS,
  EXPORT_APPOINTMENTS_ASYNC_FAILURE,
  EXPORT_APPOINTMENTS_PROGRESS_UPDATE,
  EXPORT_APPOINTMENTS_POLLING_COMPLETE,
  RESET_EXPORT_STATE,
  FETCH_ALL_APPOINTMENTS_FAILURE,
  FETCH_ALL_APPOINTMENTS_START,
  FETCH_ALL_APPOINTMENTS_SUCCESS,
  FETCH_PAGINATED_CLIENTS_FAILURE,
  FETCH_PAGINATED_CLIENTS_START,
  FETCH_PAGINATED_CLIENTS_SUCCESS,
  FETCH_PROVIDER_AVAILABILITY_SUCCESS,
  RESET_APPOINTMENTS_STATE,
  RESET_CLIENTS_AVAILABILITY,
  RESET_FILTERS,
  RESET_PROVIDER_AVAILABILITY,
  RESET_SELECTED_APPOINTMENT_ID,
  RESET_SPECIFIC_DAYS_APPOINTMENTS_STATE,
  SET_PROVIDER_DETAILS,
  SET_SELECTED_APPOINTMENT_ID,
  UPDATE_APPOINTMENT_SUCCESS,
  UPDATE_FETCHED_PROVIDERS,
  FETCH_APPOINTMENT_ACTIVITY_LOGS_START,
  FETCH_APPOINTMENT_ACTIVITY_LOGS_SUCCESS,
  FETCH_APPOINTMENT_ACTIVITY_LOGS_FAILURE,
  RESET_APPOINTMENT_ACTIVITY_LOGS,
} from './calendar.constants';
import {
  Appointment,
  AppointmentMap,
  AppointmentsActions,
  AppointmentsStateInfo,
  AvailabilityInfoState,
  ClientsActions,
  ClientsAvailabilityState,
  ClientsDictionary,
  ProviderAvailabilityActions,
  ProviderEntityTypes,
  ProvidersModelTypes,
  // Activity Logs
  ActivityLogsActions,
  ActivityLogsState,
  // Async Export
  ExportState,
  ExportStatus,
} from './calendar.types';
import {ExportAsyncActions} from './calendar-actions.types';
import {groupAppointmentsByDate, groupClientsById} from './helper';

// Provider availability state
const availabilityInitialState = {
  providerDetails: undefined,
  category: '',
  slots: {},
  appointmentTypeId: '',
  appointmentTypeName: '',
  providers: {
    [ProvidersModelTypes.therapists]: {entities: {}},
    [ProvidersModelTypes.prescribers]: {entities: {}},
  },
  isFetchingProvider: false,
};

function providerAvailability(
  state: AvailabilityInfoState = availabilityInitialState,
  action: ProviderAvailabilityActions,
): AvailabilityInfoState {
  switch (action.type) {
    case RESET_PROVIDER_AVAILABILITY: {
      return availabilityInitialState;
    }
    case FETCH_PROVIDER_AVAILABILITY_SUCCESS: {
      const {
        providerId,
        category,
        appointmentTypeId,
        appointmentTypeName,
        slots,
      } = action.payload;
      return {
        ...state,
        category,
        appointmentTypeId,
        appointmentTypeName,
        slots: {
          ...state.slots,
          [providerId]: slots,
        },
      };
    }
    case SET_PROVIDER_DETAILS: {
      const {providerModelType, providerId} = action.payload;
      const provider =
        state.providers[providerModelType]?.entities?.[providerId];
      if (!provider) return state;
      return {
        ...state,
        providerDetails: provider,
      };
    }
    case UPDATE_FETCHED_PROVIDERS: {
      const {filter, entities, pagination} = action.payload;
      const typedEntities = entities as ProviderEntityTypes;

      if (!typedEntities[filter]) return state;

      const current = state.providers[filter]!;
      const newEntities = typedEntities[filter];

      const mergedEntities = {
        ...current.entities,
      };

      // Only update or add the fetched ones
      for (const id in newEntities) {
        mergedEntities[id] = newEntities[id]; // replace if exists, add if not
      }

      return {
        ...state,
        providers: {
          ...state.providers,
          [filter]: {
            pagination: pagination ?? current.pagination,
            entities: mergedEntities,
          },
        },
      };
    }
    default: {
      return state;
    }
  }
}

const initialPaginationState = {
  currentPage: 1,
  totalItems: 0,
  hasNextPage: false,
  pageSize: APPOINTMENTS_MAX_SIZE,
};

const initialQueryKeysState = {
  searchTerm: '',
  providerId: '',
  from: '',
  to: '',
  country: '',
  timezone: '',
};

// Appointment state
const appointmentsInitialState = {
  collections: [] as Appointment[],
  groupedCollections: {} as AppointmentMap,
  selectedAppointmentId: '',
  pagination: initialPaginationState,
  isLoadingAppointments: false,
  queryKeys: initialQueryKeysState,
  loadingStatusMap: {},
  lastResetAt: null,
};
function appointments(
  state: AppointmentsStateInfo = appointmentsInitialState,
  action: AppointmentsActions,
): AppointmentsStateInfo {
  switch (action.type) {
    case RESET_APPOINTMENTS_STATE: {
      return {
        ...appointmentsInitialState,
        lastResetAt: Date.now(),
      };
    }
    case RESET_FILTERS: {
      return {
        ...state,
        queryKeys: appointmentsInitialState.queryKeys,
      };
    }
    case RESET_SPECIFIC_DAYS_APPOINTMENTS_STATE: {
      const date = formatDate(action.payload);

      // Remove from the collection
      const collections = state.collections.filter(
        a => formatDate(new Date(a.dateOfAppointment)) !== date,
      );

      // Remove from the grouped collection
      const {[date]: removed, ...groupedCollections} = state.groupedCollections;

      // Remove loading status
      const {[date]: _, ...loadingStatusMap} = state.loadingStatusMap;

      return {
        ...state,
        collections,
        groupedCollections,
        loadingStatusMap,
        pagination: initialPaginationState,
        queryKeys: initialQueryKeysState,
      };
    }
    case FETCH_ALL_APPOINTMENTS_START: {
      const {from} = action.payload;
      return {
        ...state,
        isLoadingAppointments: true,
        loadingStatusMap: {
          ...state.loadingStatusMap,
          [from]: state.loadingStatusMap[from] ?? true,
        },
      };
    }
    case FETCH_ALL_APPOINTMENTS_FAILURE: {
      const queryKeys = action.payload;
      const failedDate = queryKeys?.from;

      return {
        ...state,
        isLoadingAppointments: false,
        loadingStatusMap: failedDate
          ? {
              ...state.loadingStatusMap,
              [failedDate]: false,
            }
          : state.loadingStatusMap,
      };
    }
    case FETCH_ALL_APPOINTMENTS_SUCCESS: {
      const {
        appointments,
        pagination,
        filter: isFilteredData,
        queryKeys,
      } = action.payload;

      // Get active date
      const activeDate = queryKeys?.from;

      // Check if it's the first page
      const isFirstPage =
        pagination.currentPage === 1 || state.collections.length === 0;

      // Set collections
      const collections =
        isFirstPage || state.queryKeys?.from === activeDate
          ? state.collections.concat(appointments)
          : state.collections;

      // Generate grouped collections
      const groupedAppointments = groupAppointmentsByDate(appointments);

      // Copy existing grouped collections to prepare for merge
      const mergedGroupedCollections: AppointmentMap = {
        ...state.groupedCollections,
      };

      // Determine queryKeys to use — fallback to initial state if not filtered
      const queryData =
        isFilteredData && queryKeys
          ? queryKeys
          : appointmentsInitialState.queryKeys;

      // Merge active date's appointments (with updated pagination & queryKeys)
      if (activeDate && groupedAppointments[activeDate]) {
        const prevGroup = mergedGroupedCollections[activeDate] || {
          appointments: [],
          pagination: appointmentsInitialState.pagination,
          queryKeys: appointmentsInitialState.queryKeys,
        };

        // Get unique appointment IDs from previous group
        const existingIds = new Set(
          prevGroup.appointments.map(appt => appt.appointmentID),
        );

        // Filter out duplicate appointments for the active date
        const uniqueAppointments = groupedAppointments[activeDate].filter(
          appt => !existingIds.has(appt.appointmentID),
        );

        // Merge new unique appointments into the active date group
        mergedGroupedCollections[activeDate] = {
          appointments: prevGroup.appointments.concat(uniqueAppointments),
          pagination,
          queryKeys: queryData, // Store the latest queryKeys
        };
      }

      // merge other dates (keep their original pagination and queryKeys)
      Object.entries(groupedAppointments).forEach(([date, newAppts]) => {
        if (date !== activeDate) {
          const prevGroup = mergedGroupedCollections[date];

          if (prevGroup) {
            // Get existing IDs and deduplicate
            const existingIds = new Set(
              prevGroup.appointments.map(a => a.appointmentID),
            );
            const uniqueAppointments = newAppts.filter(
              appt => !existingIds.has(appt.appointmentID),
            );

            // Merge into existing group without updating pagination/queryKeys
            mergedGroupedCollections[date] = {
              appointments: prevGroup.appointments.concat(uniqueAppointments),
              pagination: prevGroup.pagination,
              queryKeys: prevGroup.queryKeys,
            };
          } else {
            // Create new group for the date if it doesn't exist yet
            mergedGroupedCollections[date] = {
              appointments: newAppts,
              pagination: appointmentsInitialState.pagination,
              queryKeys: queryData,
            };
          }
        }
      });

      return {
        ...state,
        collections,
        groupedCollections: mergedGroupedCollections,
        pagination,
        queryKeys: queryData,
        isLoadingAppointments: false,
        loadingStatusMap: {
          ...state.loadingStatusMap,
          [(activeDate as string) ?? '']: false, // mark that date as no longer "initial loading"
        },
      };
    }
    case SET_SELECTED_APPOINTMENT_ID: {
      return {
        ...state,
        selectedAppointmentId: action.payload,
      };
    }
    case RESET_SELECTED_APPOINTMENT_ID: {
      return {
        ...state,
        selectedAppointmentId: '',
      };
    }
    case UPDATE_APPOINTMENT_SUCCESS:
    case CANCEL_APPOINTMENT_SUCCESS: {
      const updatedAppointment = action.payload;
      const appointmentDateKey = formatDateWithoutTimezone(
        updatedAppointment.datetime,
      );
      // Update collections (flat list)
      const updatedCollections = state.collections.map(appt =>
        appt.appointmentID === updatedAppointment.id
          ? {
              ...appt,
              canceled: updatedAppointment.canceled,
              labels: updatedAppointment.labels,
            }
          : appt,
      );
      // Update groupedCollections only if date group exists
      const existingGroup = state.groupedCollections[appointmentDateKey];
      const updatedGroup = existingGroup
        ? {
            ...existingGroup,
            appointments: existingGroup.appointments.map(appt =>
              appt.appointmentID === updatedAppointment.id
                ? {
                    ...appt,
                    canceled: updatedAppointment.canceled,
                    labels: updatedAppointment.labels,
                  }
                : appt,
            ),
          }
        : state.groupedCollections[appointmentDateKey];
      return {
        ...state,
        collections: updatedCollections,
        groupedCollections: {
          ...state.groupedCollections,
          ...(updatedGroup && {
            [appointmentDateKey]: updatedGroup,
          }),
        },
      };
    }

    default:
      return state;
  }
}

const clientsAvailabilityInitialState: ClientsAvailabilityState = {
  clientsList: {} as ClientsDictionary,
  pagination: {
    currentPage: 1,
    resultsPerPage: 100,
    totalResults: 0,
    totalPages: 0,
    hasMore: false,
  },
  isLoading: false,
};

const clientsAvailability = (
  state: ClientsAvailabilityState = clientsAvailabilityInitialState,
  action: ClientsActions,
): ClientsAvailabilityState => {
  switch (action.type) {
    case RESET_CLIENTS_AVAILABILITY: {
      return clientsAvailabilityInitialState;
    }
    case FETCH_PAGINATED_CLIENTS_START: {
      return {...state, isLoading: true};
    }
    case FETCH_PAGINATED_CLIENTS_FAILURE: {
      return {...state, isLoading: false};
    }
    case FETCH_PAGINATED_CLIENTS_SUCCESS: {
      const {results, pagination} = action.payload;
      const groupedClientsById = groupClientsById(results);

      const clientsList =
        pagination?.currentPage === 1 || results.length === 0
          ? groupedClientsById
          : {...state.clientsList, ...groupedClientsById};

      return {
        ...state,
        clientsList,
        pagination,
        isLoading: false,
      };
    }
    default:
      return state;
  }
};

const activityLogsInitialState: ActivityLogsState = {
  byBookingUid: {},
  isLoading: false,
};

function activityLogs(
  state: ActivityLogsState = activityLogsInitialState,
  action: ActivityLogsActions,
): ActivityLogsState {
  switch (action.type) {
    case RESET_APPOINTMENT_ACTIVITY_LOGS: {
      return activityLogsInitialState;
    }
    case FETCH_APPOINTMENT_ACTIVITY_LOGS_START: {
      return {
        ...state,
        isLoading: true,
      };
    }
    case FETCH_APPOINTMENT_ACTIVITY_LOGS_SUCCESS: {
      const {calcomBookingUid, response} = action.payload;
      return {
        ...state,
        isLoading: false,
        byBookingUid: {
          ...state.byBookingUid,
          [calcomBookingUid]: response,
        },
      };
    }
    case FETCH_APPOINTMENT_ACTIVITY_LOGS_FAILURE: {
      return {
        ...state,
        isLoading: false,
      };
    }
    default: {
      return state;
    }
  }
}

const exportInitialState: ExportState = {
  exportId: null,
  status: ExportStatus.IDLE,
  progressPercentage: 0,
  totalRecords: 0,
  totalChunks: 0,
  completedChunks: 0,
  failedChunks: 0,
  csvDownloadUrl: null,
  failedReason: null,
  isPolling: false,
};

function exportAsync(
  state: ExportState = exportInitialState,
  action: ExportAsyncActions,
): ExportState {
  switch (action.type) {
    case RESET_EXPORT_STATE: {
      return exportInitialState;
    }
    case EXPORT_APPOINTMENTS_ASYNC_START: {
      return {
        ...exportInitialState,
        status: ExportStatus.QUEUED,
        isPolling: false,
      };
    }
    case EXPORT_APPOINTMENTS_ASYNC_SUCCESS: {
      const {exportId} = action.payload;
      return {
        ...state,
        exportId,
        status: ExportStatus.PROCESSING,
        isPolling: true,
      };
    }
    case EXPORT_APPOINTMENTS_PROGRESS_UPDATE: {
      const {
        exportStatus,
        progressPercentage,
        totalRecords,
        totalChunks,
        completedChunks,
        failedChunks,
      } = action.payload;
      return {
        ...state,
        status: exportStatus,
        progressPercentage,
        totalRecords,
        totalChunks,
        completedChunks,
        failedChunks,
      };
    }
    case EXPORT_APPOINTMENTS_POLLING_COMPLETE: {
      const {csvDownloadUrl, failedReason} = action.payload;
      return {
        ...state,
        status: ExportStatus.COMPLETED,
        csvDownloadUrl: csvDownloadUrl || null,
        failedReason: failedReason || null,
        isPolling: false,
      };
    }
    case EXPORT_APPOINTMENTS_ASYNC_FAILURE: {
      return {
        ...state,
        status: ExportStatus.FAILED,
        failedReason: action.payload || 'Export failed',
        isPolling: false,
      };
    }
    default: {
      return state;
    }
  }
}

const calendarReducer = combineReducers({
  availability: providerAvailability,
  appointments,
  clients: clientsAvailability,
  activityLogs,
  exportAsync,
});

export default calendarReducer;
